/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { Text, Title } from '@components/v2/ui';
import {
	Box,
	Group,
	ScrollArea,
	Table,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import classes from './ProductionsListFromFile.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
interface ProductionsListProps {
	addedProductions: any;
	selectedColumns: any[];
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ProductionsListFromFile = ({
	addedProductions,
	selectedColumns,
}: ProductionsListProps) => {
	const { t, i18n } = useTranslation();

	const productions =
		addedProductions &&
		Array.isArray(addedProductions) &&
		addedProductions.length > 0 &&
		addedProductions.map(
			(prod: { [x: string]: string }) => {
				return selectedColumns.reduce((acc, col) => {
					acc[col.value] = prod[col.columnsCSV];
					return acc;
				}, {});
			}
		);

	const rows =
		productions.length > 0 &&
		productions.map((item: any, index: number) => (
			<Table.Tr
				key={item.id || index}
				className={classes.tableRow}
			>
				<Table.Td>
					<Text variant="body2" c="#1D3965" fw={500}>
						{item.quantity}
					</Text>
				</Table.Td>
				<Table.Td>
					<Group gap={4}>
						<Text variant="body2" c="#9BA6B7">
							{t('global.from')}
						</Text>
						<Text variant="body2" c="#1D3965" fw={500}>
							{item.startDate
								? dayjs(item.startDate).format(
										'DD-MM-YYYY'
									)
								: '--'}
						</Text>
						<Text variant="body2" c="#9BA6B7">
							{t('global.to')}
						</Text>
						<Text variant="body2" c="#1D3965" fw={500}>
							{item.endDate
								? dayjs(item.endDate).format('DD-MM-YYYY')
								: item.startDate
									? dayjs(item.startDate)
											.add(1, 'day')
											.format('DD-MM-YYYY')
									: '--'}
						</Text>
					</Group>
				</Table.Td>
				<Table.Td>
					<Text variant="body2" c="#1D3965" fw={500}>
						{item.conversionFactorOverride || '--'}
					</Text>
				</Table.Td>
				<Table.Td>
					<Text
						variant="body2"
						c="#1D3965"
						fw={500}
						className={classes.truncateText}
					>
						{item.proof || '--'}
					</Text>
				</Table.Td>
			</Table.Tr>
		));

	return (
		<Box className={classes.root}>
			<div className={classes.header}>
				<Title c={'#1D3965'} order={6} ml={'sm'}>
					{`${t('global.addedProductions')} (${addedProductions.length})`}
				</Title>
			</div>

			<ScrollArea w={'100%'} type="always">
				<Table className={classes.table}>
					<Table.Thead>
						<Table.Tr>
							<Table.Th
								className={classes.tableHeaderCell}
							>
								{t('global.production')}
							</Table.Th>
							<Table.Th
								className={classes.tableHeaderCell}
							>
								{t('global.productionPeriod')}
							</Table.Th>
							<Table.Th
								className={classes.tableHeaderCell}
							>
								{t('global.factor')}
							</Table.Th>
							<Table.Th
								className={classes.tableHeaderCell}
							>
								{i18n.language == 'fr'
									? 'Preuve'
									: 'Proof'}
							</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>{rows}</Table.Tbody>
				</Table>
			</ScrollArea>
		</Box>
	);
};
